import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { logWarn } from '../utils/log';

const STORAGE_KEY = 'flowcore_session_token';

const AuthContext = createContext(null);

function parseHashParams() {
  const raw = window.location.hash.replace(/^#/, '');
  if (!raw) return {};
  try {
    return Object.fromEntries(new URLSearchParams(raw));
  } catch (e) {
    logWarn('auth', 'hash parse:', e?.message || e);
    return {};
  }
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => sessionStorage.getItem(STORAGE_KEY) || null);
  const [authError, setAuthError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const params = parseHashParams();
    if (params.auth_error) {
      setAuthError(params.auth_error);
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    if (params.access_token) {
      const t = params.access_token;
      sessionStorage.setItem(STORAGE_KEY, t);
      setToken(t);
      setAuthError(null);
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    setIsLoaded(true);
  }, []);

  const getToken = useCallback(async () => token, [token]);

  const signOut = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY);
    setToken(null);
    setAuthError(null);
  }, []);

  const setSessionToken = useCallback((t) => {
    if (!t) return;
    sessionStorage.setItem(STORAGE_KEY, t);
    setToken(t);
    setAuthError(null);
  }, []);

  const value = useMemo(
    () => ({
      isLoaded,
      isSignedIn: Boolean(token),
      token,
      authError,
      clearAuthError: () => setAuthError(null),
      getToken,
      signOut,
      setSessionToken
    }),
    [isLoaded, token, authError, getToken, signOut, setSessionToken]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuthContext must be used within AuthProvider');
  }
  return ctx;
}
